import css from 'components/Pages/PurchaseForm/Tickets/Tickets.module.scss'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ComponentColor, IEventDetails } from 'meta/backendControlled/structure'
import DataTestId from 'meta/dataTestId'
import { FormFieldType, IFormField } from 'meta/form'
import { Breakpoints } from 'meta/layout/breakpoints'
import { ITicket } from 'meta/pages/tickets'
import { QueryParams, Routes } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'
import { getFastCheckoutUserObject, setPurchaseFormCookieString } from 'utils/cookie/client'
import { generateRandomString } from 'utils/string'

import { ButtonRed } from 'components/Actions/Action'
import Form, { IFormValues } from 'components/Forms/Form'
import { Grid, GridColumn, GridContainer } from 'components/Layout/Grid'
import EventDetails from 'components/Pages/PurchaseForm/Tickets/EventDetails'
import { useFormFields, useTicketsForm } from 'components/Pages/PurchaseForm/Tickets/hooks'
import TicketsRecap from 'components/Pages/PurchaseForm/Tickets/Recap'
import Ticket from 'components/Pages/PurchaseForm/Tickets/Ticket'
import { ITicketFormItem } from 'components/Pages/PurchaseForm/Tickets/types'
import BalloonBoxModal from 'components/TextBoxes/BalloonBoxModal'
import BalloonBoxModalNoPortal from 'components/TextBoxes/BalloonBoxModalNoPortal'
import { useAuthToken, useUserData } from 'hooks/useAuth'

import ExtraGoods from './ExtraGoods'
import { checkIfTransactionIsGoodsRelated } from './utils'

interface IPropsTickets {
  transactionCode: string
  transactionToken: string
  tickets: Array<ITicket>
  event: IEventDetails
  missingUserInfos: boolean
}

interface IPostResponse {
  notificationDeliveryUuid: string
}

const Tickets: React.FC<IPropsTickets> = (props) => {
  const { event, missingUserInfos, tickets, transactionCode, transactionToken } = props

  const authToken = useAuthToken()
  const userData = useUserData()
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showSecondaryTicketingBox, setShowSecondaryTicketingBox] = useState<boolean>(false)
  const [secondaryTickets, setSecondaryTickets] = useState<number[]>([])

  const isTransactionGoodsRelated =
    event && event.policyResponse && checkIfTransactionIsGoodsRelated(event.policyResponse?.code)

  const handleMissingUserInfos = async () => {
    await setPurchaseFormCookieString(transactionCode, transactionToken)
    window.location.replace(Routes.ProfileChangeData.generatePath())
  }

  const checkSecondaryTicketingIssues = (items: Array<ITicketFormItem>): boolean => {
    const itemKeys: Array<string> = []
    let filteredItemKeys: Array<string> = []

    if (!event?.secondaryTicketing || Boolean(event.isPersonalDataRequired)) {
      return false
    }

    items.map((elem) => {
      itemKeys.push(
        `${elem.buyerData['firstName']}_${elem.buyerData['lastName']}_${elem.buyerData['phonePrefix']}_${elem.buyerData['phone']}`
      )
    })

    const tempSecondaryTickets: number[] = []

    filteredItemKeys = itemKeys.filter((item, index) => {
      const check = itemKeys.indexOf(item) === index

      if (!check) {
        tempSecondaryTickets.push(index)
      }

      return check
    })

    const areThereDuplicateTickets = itemKeys.length > filteredItemKeys.length

    if (areThereDuplicateTickets) {
      setShowSecondaryTicketingBox(true)
      setSecondaryTickets(tempSecondaryTickets)
    }

    return areThereDuplicateTickets
  }

  const { bodyParser, onSuccess } = useTicketsForm(transactionCode, transactionToken, checkSecondaryTicketingIssues)
  const formFields = useFormFields(isTransactionGoodsRelated ? tickets.slice(0, 1) : tickets, {
    policy: event?.policy,
    isPersonalDataRequired: event?.isPersonalDataRequired,
    goodsEvent: isTransactionGoodsRelated,
    userData: userData,
  })

  const getFormattedFormFields = () => {
    const fields: IFormField<FormFieldType>[] = [
      ...Object.values(formFields.commonFields).flat(),
      ...Object.values(formFields.extraFields).flat(),
      ...Object.values(formFields.personalDataFields).flat(),
    ]
    if (Object.values(formFields.covid19Fields) !== undefined) {
      Object.values(formFields.covid19Fields)
        .flat()
        .map((covidField) => {
          if (covidField?.formField) {
            fields.push(covidField.formField)
          }
        })
    }

    return fields
  }

  const bodyPreParser = (fieldValues: IFormValues): object => {
    if (isTransactionGoodsRelated) {
      const phone = fieldValues[`0_${tickets[0].id}_phone`]
      const phonePrefix = fieldValues[`0_${tickets[0].id}_phonePrefix`]

      const fullFieldValues: { [key: string]: string } = {}

      const fastCheckoutUserData = getFastCheckoutUserObject()

      if (fastCheckoutUserData) {
        tickets.map((ticket, idx) => {
          fullFieldValues[`${idx}_${ticket.id}_firstName`] = 'GoodsService'
          fullFieldValues[`${idx}_${ticket.id}_lastName`] = generateRandomString(12)
          fullFieldValues[`${idx}_${ticket.id}_phone`] = phone
          fullFieldValues[`${idx}_${ticket.id}_phonePrefix`] = phonePrefix
        })
      } else {
        tickets.map((ticket, idx) => {
          fullFieldValues[`${idx}_${ticket.id}_firstName`] = userData?.firstName ?? ''
          fullFieldValues[`${idx}_${ticket.id}_lastName`] = userData?.lastName ?? ''
          fullFieldValues[`${idx}_${ticket.id}_phone`] = phone
          fullFieldValues[`${idx}_${ticket.id}_phonePrefix`] = phonePrefix
        })
      }

      return bodyParser(fullFieldValues)
    }

    return bodyParser(fieldValues)
  }

  const handleSuccessRedirect = (values: object, response: IPostResponse) => {
    const { notificationDeliveryUuid } = response

    if (isTransactionGoodsRelated) {
      const params: QueryParams['Success'] = {
        code: event.policyResponse?.code,
      }

      const fastCheckoutUserData = getFastCheckoutUserObject()

      if (fastCheckoutUserData || (isTransactionGoodsRelated && userData)) {
        params['uuid'] = notificationDeliveryUuid ?? transactionCode
        params['fromFastCheckout'] = 'true'
      }

      navigate(Routes.Success.generatePath(undefined, params))
      return
    }
    return onSuccess()
  }

  // TODO: handle user.session
  // const { token: authTokenFromSession } = user.session

  return (
    <div className={css.ticketsContainer}>
      <Form
        action={ApiEndpoint.purchase.form()}
        //authToken={authTokenFromQueryString ? authTokenFromQueryString : authTokenFromSession}
        authToken={authToken}
        bodyParser={bodyPreParser}
        formFields={getFormattedFormFields()}
        //@ts-ignore
        onSuccess={handleSuccessRedirect}
        renderFields={false}
      >
        <GridContainer>
          <Grid className={css.tickets}>
            <GridColumn
              gridColumnStart={{
                [Breakpoints.mobile]: 4,
                [Breakpoints.tablet]: 1,
                [Breakpoints.laptop]: 1,
              }}
              gridColumnEnd={{
                [Breakpoints.mobile]: 14,
                [Breakpoints.tablet]: 17,
                [Breakpoints.laptop]: 17,
              }}
            >
              <h1 data-testid={DataTestId.purchaseForm.title}>
                {isTransactionGoodsRelated ? t('tickets:title_goods') : t('tickets:title')}
              </h1>
            </GridColumn>

            {event && tickets && tickets.length > 0 && isTransactionGoodsRelated && (
              <ExtraGoods event={event} formFields={formFields} idx={0} tickets={tickets} />
            )}

            {!isTransactionGoodsRelated && (
              <>
                {event && <EventDetails event={event} />}

                {tickets.map((ticket, idx) => (
                  <Ticket key={String(idx)} formFields={formFields} idx={idx} ticket={ticket} />
                ))}
              </>
            )}

            <TicketsRecap
              tickets={tickets}
              secondaryTickets={secondaryTickets}
              isTransactionGoodsRelated={isTransactionGoodsRelated}
            />
          </Grid>
        </GridContainer>
      </Form>

      {showSecondaryTicketingBox && (
        <BalloonBoxModal
          bgColor={ComponentColor.redLight}
          onClose={() => {
            setShowSecondaryTicketingBox(false)
          }}
          infoType={'error'}
          title={`${t('common:error')}!`}
        >
          <div>{t('tickets:secondary_ticketing_msg')}</div>
        </BalloonBoxModal>
      )}

      {missingUserInfos && (
        <BalloonBoxModalNoPortal
          bgColor={ComponentColor.white}
          infoType={'warning'}
          title={`${t('tickets:missing_user_info')}`}
        >
          <div className={css.missingUserInfos}>
            <div>{t('tickets:missing_user_info_body')}</div>
            <ButtonRed className={css.missingUserInfos__btnConfirm} onClick={handleMissingUserInfos}>
              <span>{t('common:update')}</span>
            </ButtonRed>
          </div>
        </BalloonBoxModalNoPortal>
      )}
    </div>
  )
}

export default Tickets
